export const useRegionList = async () => {
  return useFetchSafely(() => `regions`);
};

export const useClinicSearchDetail = async () => {
  return useFetchSafelyCached("pages/clinics");
};

export const usePopularCitiesList = async () => {
  return useFetchSafelyCached("popular-cities");
};

export const useClinicListsByCity = async (mapData, { locale, useErrorBoundary, retry } = {}) => {
  return useFetchSafelyCached("clinics", {
    params: mapData,
    locale,
    useErrorBoundary,
    retry,
  });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retries a useFetch result with refresh() until it has data, waiting delayMs × attempt between tries. Returns
 * whether data arrived. Use refresh() rather than calling the composable again: when a key already holds an error in
 * the page payload (e.g. the city page's SSR fetch failed), a repeat call returns that cached error without making a
 * request, whereas refresh() always fetches.
 */
export const retryUntilData = async (response, { attempts = 3, delayMs = 1000, sleep = wait } = {}) => {
  for (let attempt = 1; !response.data.value && attempt < attempts; attempt++) {
    await sleep(delayMs * attempt);
    await response.refresh();
  }
  return Boolean(response.data.value);
};

/** Coordinates from server/data/city-coordinates.json, or null if the city isn't in it. */
export const useCityCoordinates = async (slug, locale) => {
  return useFetchSafelyCached(`city-coordinates/${slug}`, { locale, useErrorBoundary: false });
};

/** Query for the clinics within a city's range — the same one components/clinic/GoogleMap.vue sends. */
export const buildCityClinicParams = ({ coordinates, range, defaultRange, clinicTypes = [] }) => {
  const params = {};
  clinicTypes.forEach((type, index) => {
    params[`clinic_type[${index}]`] = type;
  });
  params.latitude = coordinates.lat;
  params.longitude = coordinates.lng;
  params.range = range || defaultRange;
  return params;
};

// Clinics shown before "Show more". City pages show 50, so the server HTML carries a fuller list in every country;
// the shared list elsewhere (clinic search, get-started modal, test pages) keeps 10, as some of those can list every
// clinic in the country. Each "Show more" click adds CLINIC_LIST_PAGE_SIZE.
export const CLINIC_LIST_PAGE_SIZE = 10;
export const CITY_PAGE_INITIAL_CLINICS = 50;
export const initialClinicCount = (isCityPage) => (isCityPage ? CITY_PAGE_INITIAL_CLINICS : CLINIC_LIST_PAGE_SIZE);

// Cap on full list items rendered into a city page (those past "show more" are hidden until shown). The biggest UK
// city page has ~67 clinics in range after the Oct 2026 range reductions (London), so none is capped today
// (BUP-1538). REVIEW when US clinic data goes live: if US cities are as dense as Australia (Melbourne has 537 clinics
// within 30 miles), measure the HTML size of the densest US city pages and adjust this cap or the US city ranges.
// Also rerun scripts/generate-city-coordinates.mjs --locales=en-us,es-us --verify, as no US point could be checked
// before then.
export const MAX_FULL_CITY_CLINICS = 100;

/**
 * Splits a clinic list for rendering. `full` is rendered as full list items (MapList hides those past the visible
 * count); `hiddenLinks` are plain hidden links. With `withHidden` (city pages where clinic pages exist), every clinic
 * is rendered in full up to `maxFull`, so crawlers see each clinic and its link with its address; any beyond the cap
 * become plain links. Without it, only the visible page is rendered. Accepts MapList's `{ data: "empty" }` marker.
 */
export const splitClinicPage = (clinics, visibleCount, withHidden, maxFull = MAX_FULL_CITY_CLINICS) => {
  if (!Array.isArray(clinics)) return { full: [], hiddenLinks: [] };
  if (!withHidden) return { full: clinics.slice(0, visibleCount), hiddenLinks: [] };
  const full = clinics.slice(0, Math.max(visibleCount, maxFull));
  return { full, hiddenLinks: clinics.slice(full.length).filter((clinic) => clinic.slug) };
};

/**
 * The city page's SSR clinic list, or null when the search found nothing. City pages keep their stored coordinates
 * even when nothing is in range, because clinics close and reopen; but an empty result can also mean a wrong stored
 * point (e.g. Gibraltar resolved 17 km into Spain), so on null the page lets the map geocode the city in the browser.
 */
export const ssrCityClinicList = (entries) =>
  Array.isArray(entries) && entries.length ? entries.map(toClinicListItem) : null;

/** Maps a clinic from the clinics API to the shape components/clinic/LocationList.vue renders. */
export const toClinicListItem = (entry) => ({
  id: entry.id,
  title: entry.display_title,
  slug: entry.slug,
  // HTML parsing turns CRLF into LF, so an un-normalised CMS address mismatches on hydration.
  content: entry.show_address === true ? (entry.address ?? "").replace(/\r\n?/g, "\n") : "",
  distance: entry.distance,
  lat: Number(entry.latitude),
  lng: Number(entry.longitude),
  sites: entry?.sites,
  clinics: entry?.clinics,
  clinic_type: entry?.clinic_type,
  show_address: entry?.show_address,
});

export const useCityDetailPage = async (slug) => {
  return useFetchSafely(() => `city/${slug}`);
};

export const useFetchClinicDetailBySlug = async (slug) => {
  return useFetchSafely(() => `clinics/${slug}`);
};

export const useBlockReview = async (slug, locale) => {
  return useFetchSafelyCached("customer-reviews/reviews", {
    params: { block: slug },
    locale,
  });
};

export const useClinicSiteMarkers = async () => {
  //return useFetchSafelyCached("clinic-site-markers");
  return useFetchSafely(() => `clinic-site-markers`);
};
